import { TimeUnit } from 'chart.js';
import { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { ExtendedDataProvider } from '../../types';

const useGetGraphStatistics = (filterValues: any, setFilterValues: Function, pov: 'CONSUMER' | 'SERVICE_OWNER', allTeams = false) => {
  const [graphIntervalUnit, setGraphIntervalUnit] = useState<TimeUnit>('month')
  const dataProvider: ExtendedDataProvider = useDataProvider()
  const [isLoaded, setIsLoaded] = useState(false)
  /* A failed request used to be indistinguishable from an empty one: the hook
     logged, set `isLoaded`, and left the data at `{}` — which the chart reads
     as "no data available". A reader was told the team had made no changes
     when in fact the server had not answered. */
  const [error, setError] = useState<string | null>(null)
  const [avgTimeCompletionGraphData, setAvgTimeCompletionGraphData] = useState<any>({})
  const [processedChangesGraphData, setProcessedChangesGraphData] = useState<any>({})
  const timeUnits = { 'month': 'monthly', 'day': 'daily', 'week': 'weekly', 'year': 'yearly' }

  const getYAxisScale = (values: number[]) => {
    // `Math.max()` of nothing is -Infinity, which then satisfies every branch
    // below and picks a unit for a series that has no values in it
    if (values.length === 0) {
      return { label: "Seconds", data: values };
    }
    const yScale = Math.max(...values)
    const rangeCoef = 4

    if (yScale/rangeCoef <= 60) {
      // less than a minute
      return { label: "Seconds", data: values };

    } else if (yScale/rangeCoef <= (60 * 60)) {
      // less than an hour
      return { label: "Minutes", data: values.map((v) =>  v/60 ) }

    } else if (yScale/rangeCoef <= (60 * 60 * 24)) {
      // less than a day
      return { label: "Hours", data: values.map((v) =>  v/(3600) ) }
    }

    return { label: "Days", data: values.map((v) =>  v/(60*60*24) ) }
  }

  useEffect(() => {
    const endpoint = pov === 'CONSUMER' ? 'consumer_statistics' : 'serviceowner_statistics'

    setIsLoaded(false)
    setError(null)
    dataProvider.getAction(endpoint,
    {
      verb: 'graphs',
      filter: {
        ...filterValues,
        ...(allTeams ? { 'all_teams': true } : {}),
        'time_range': '1y',
        /**@ts-ignore */
        'time_interval': timeUnits[graphIntervalUnit]
      }
    })

      .then((response: any) => {
        const labels: any = []
        const _completionAvgValues: any = []
        const _totalCompletedValues: any = []
        ;(response.data?.cis_completed || []).map((entry: any) => {
          _completionAvgValues.push(entry?.average)
          _totalCompletedValues.push(entry?.count)
          const newDate = new Date(entry?.interval_datetime)
          if (graphIntervalUnit == 'month') {
            newDate.setDate(1)
          }
          labels.push(newDate.toLocaleDateString())
        })

        const label_data = getYAxisScale(_completionAvgValues)
        setAvgTimeCompletionGraphData({
          labels,
          datasets: [{
            label: 'Average',
            tension: 0,
            data: label_data.data,
            borderColor: '#5796cc',
          }],
          yLabel: label_data.label
        })

        setProcessedChangesGraphData({
          labels,
          datasets: [{
            label: 'Changes created',
            data: _totalCompletedValues,
            backgroundColor: 'rgba(255, 99, 132, 0.5)',
          }],
        })

        setIsLoaded(true)
      })
      .catch((err: any) => {
        console.error(err)
        setAvgTimeCompletionGraphData({})
        setProcessedChangesGraphData({})
        setError('Metrics could not be loaded.')
        setIsLoaded(true)
      })


  }, [pov, allTeams, graphIntervalUnit, filterValues])

  return {
    setGraphIntervalUnit,
    graphIntervalUnit,
    avgTimeCompletionGraphData,
    processedChangesGraphData,
    isLoaded,
    error,
    setFilterValues
  }

}

export default useGetGraphStatistics;